import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { Tab } from 'stores/inboundReferralsStore';
import { colors } from 'styles/theme/colors';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';

import { INBOUND_REFERRALS_ROUTES, selectedGroupName } from '../constants';
import {
  categorySegments,
  LIST_STATUS_DISPLAY,
  overallListStatus,
  progressSummary,
  referralDecidedAt,
} from '../referralProgress';
import { sentToSummary } from '../ReferralSentTo';
import { TONE_STYLES } from '../StatusBadge';

import {
  Bar,
  DecisionDuration,
  ElapsedDuration,
  isDiscovery,
  Pill,
  ProgressLabel,
  Segment,
  SelectedAtDuration,
  subjectLabel,
} from './referralListCells';

// One referral as a card, for the inbox below the mobile breakpoint. The desktop table's twelve
// columns total roughly 1,800px of minimum width — far past a phone — and the mockups answer that
// with a card rather than a narrower table, so there is no grid-template config that produces this.
//
// Reads the same helpers the table columns do, so a card and a row can't disagree about a referral's
// status or progress.
//
// Tab-aware for the same reason the columns are (inboundReferralsTableColumns): the three tabs ask
// different questions, so they show different facts. Awaiting Admission drops progress and status —
// a placed referral waiting on a census event has no meaningful step left — and measures from the
// selection rather than from creation, which is also that tab's sort key, so the card has to show
// the value the list is ordered by. History drops progress too (every row is finished) and reports
// the span to the decision plus the date it concluded.

export default function ReferralCard({ workflow, tab }: { workflow: Workflow; tab: Tab }) {
  const to = `${INBOUND_REFERRALS_ROUTES.BASE}/${workflow.id}`;
  const discovery = isDiscovery(workflow);
  const { index, total, categoryName } = progressSummary(workflow);
  const segments = categorySegments(workflow);

  const awaiting = tab === 'awaitingAdmission';
  const history = tab === 'history';
  // Progress and the lifecycle status are an Active-tab reading. Both other tabs drop them in the
  // table, for reasons that apply identically here.
  const showProgress = !awaiting && !history;
  const showStatus = !awaiting;

  // The same rollup the table's Status column reads, archived included — overallListStatus already
  // reports an archived run as 'archived' rather than its pre-archive lifecycle state.
  const listStatus = overallListStatus(workflow);
  const status = LIST_STATUS_DISPLAY[listStatus];

  return (
    <Card to={to} data-testid='referral-card'>
      <TopRow>
        {discovery ? (
          <SweepName data-testid='workflow-type'>{subjectLabel(workflow)}</SweepName>
        ) : (
          <PatientName>{subjectLabel(workflow)}</PatientName>
        )}
        {showStatus && (
          <Pill $tone={status.tone} data-testid='list-status'>
            {listStatus === 'processing' && (
              <AISparkleIcon width={12} height={12} color={TONE_STYLES[status.tone].color} />
            )}
            {status.label}
          </Pill>
        )}
      </TopRow>

      {!discovery && <Facility>{facilityLine(workflow, awaiting)}</Facility>}

      {showProgress && segments.length > 0 && (
        <Progress>
          {/* Fluid segments: the card has the full width to fill, where a table column does not. */}
          <Bar data-testid='progress-bar'>
            {segments.map((segment) => (
              <Segment
                key={segment.key}
                data-testid='progress-segment'
                $fluid
                $color={TONE_STYLES[LIST_STATUS_DISPLAY[segment.status].tone].accent}
              />
            ))}
          </Bar>
          <ProgressLabel>{total > 0 ? `${index} of ${total} – ${categoryName}` : '—'}</ProgressLabel>
        </Progress>
      )}

      <Elapsed data-testid='card-elapsed'>
        {awaiting && <SelectedAtDuration selectedAt={workflow.selectedAt} />}
        {history && <HistorySpan workflow={workflow} />}
        {!awaiting && !history && (
          <ElapsedDuration created={workflow.createdAt} decidedAt={referralDecidedAt(workflow)} />
        )}
      </Elapsed>
    </Card>
  );
}

// History's Time to Decision and Date Completed, the pair the table shows in place of a running
// clock. Either can be missing on its own: a referral canceled before any building decided has a
// completion date but no decision.
function HistorySpan({ workflow }: { workflow: Workflow }) {
  const { createdAt, concludedAt } = workflow;
  const decidedAt = referralDecidedAt(workflow);
  if (!createdAt || (!decidedAt && !concludedAt)) return <>—</>;
  return (
    <>
      <DecisionDuration created={createdAt} decidedAt={decidedAt} />
      {concludedAt && ` · completed ${simpleDate(concludedAt)}`}
    </>
  );
}

// The facility line. A single building names itself; several summarise, with the selected one
// named when there is one — the table reaches the same answer through a slideout the card has no
// room for, so the card states it outright instead.
//
// On Awaiting Admission it names the winner ALONE, matching that tab's Selected SNF column: there is
// one winner per row there, and which buildings it was also sent to is History's question.
function facilityLine(workflow: Workflow, awaiting: boolean): string {
  const selected = selectedGroupName(workflow);
  if (awaiting) return selected ?? '—';

  const groups = workflow.groups;
  if (groups.length === 0) return '—';
  if (groups.length === 1) return groups[0].name;

  return selected ? `${sentToSummary(groups)} — Selected: ${selected}` : sentToSummary(groups);
}

// The whole card navigates, not just the name: a 343px-wide card whose only target is one line of
// text is a small thing to hit, and everything on the card is about the one referral it opens.
// Hence a Link rather than a div with an inner link — it keeps the card keyboard-reachable and
// middle-clickable, and avoids nesting interactive elements inside one another.
const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border: 1px solid ${colors.black10};
  border-radius: 8px;
  background: ${colors.white};
  text-decoration: none;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${colors.blue100};
    outline-offset: 2px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

// Styled as a link because it still reads as the thing you are opening, but it is plain text — the
// card around it carries the navigation.
const PatientName = styled.span`
  font-size: 14px;
  font-weight: ${FONT_WEIGHT_MEDIUM};
  color: ${colors.blue100};
  /* A long name must not push the status pill off the row. */
  min-width: 0;
  overflow-wrap: anywhere;
`;

const SweepName = styled(PatientName)`
  font-style: italic;
  color: ${colors.black50};
`;

const Facility = styled(BodySmall)`
  color: ${colors.black100};
  font-size: 14px;
  overflow-wrap: anywhere;
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Sits apart from the progress bar above it rather than in the card's even rhythm: it is the one
// line that is about the referral's age rather than its state, and reading as part of the progress
// block made it look like a label for the bar.
const Elapsed = styled.div`
  margin-top: 8px;
  font-size: 12px;
`;
